/**
 * Tauri desktop glue (apps/desktop/INTEGRATION.md). Everything is a no-op in a normal browser: the Tauri API is
 * imported lazily and only when the page runs inside the desktop app.
 */
import type { UpdateStatus } from "@bundle/core";

/** What the desktop injects as `window.__BUNDLE_DESKTOP__` before any app script runs (INTEGRATION.md §1). */
export interface DesktopInfo {
  platform: "macos" | "windows" | "linux";
  windowLabel: string;
  vibrancy: boolean;
  version: string;
  updater: boolean;
  /** The git commit the desktop shell was built from (absent on builds older than 0.2.21). */
  commit?: string;
  /** "stable" (release builds) or "dev" (debug / `tauri dev`). */
  channel?: "stable" | "dev";
  /** CPU architecture, e.g. "aarch64", "x86_64". */
  arch?: string;
  /** Updated live by the app (traffic lights measured; inset 0 in fullscreen). Style with the CSS variables. */
  titlebar: { height: number; trafficLightsInset: number; fullscreen?: boolean };
  /** API origin (no trailing slash). The bundled page is served from tauri://localhost, so relative URLs won't do. */
  apiUrl?: string;
  /** Web app origin (no trailing slash), for links meant for a browser. */
  webUrl?: string;
}

declare global {
  interface Window {
    __BUNDLE_DESKTOP__?: DesktopInfo;
  }
}

/** Running inside the Bundle desktop app (Tauri 2 injects __TAURI_INTERNALS__). */
export function isDesktop(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** The desktop OS whose settings hold Bundle's notification switch (macOS, Windows); undefined elsewhere. */
export function notificationsOs(): "macos" | "windows" | undefined {
  const p = desktopInfo()?.platform;
  return p === "macos" || p === "windows" ? p : undefined;
}

export function desktopInfo(): DesktopInfo | null {
  return isDesktop() ? (window.__BUNDLE_DESKTOP__ ?? null) : null;
}

// ---- Auto-update (Rust checks/downloads; the shell offers "Restart") — docs/RELEASES.md → Auto-update ----

export interface DesktopUpdateReady {
  version: string;
  notes?: string | null;
}

/** `bundle:update-ready` (sent to every window), plus `update_status` for a shell that mounts after it fired. */
export async function onDesktopUpdateReady(cb: (u: DesktopUpdateReady) => void): Promise<() => void> {
  if (!isDesktop()) return () => {};
  const [{ listen }, { invoke }] = await Promise.all([import("@tauri-apps/api/event"), import("@tauri-apps/api/core")]);
  const unlisten = await listen<DesktopUpdateReady>("bundle:update-ready", (e) => cb(e.payload));
  try {
    const ready = await invoke<DesktopUpdateReady | null>("update_status");
    if (ready !== null) cb(ready);
  } catch {
    // Older desktop builds without update_status: the event still arrives.
  }
  return unlisten;
}

/** `bundle:update-status` (every window) / `update_get_status`: the updater's state, shaped like core UpdateStatus. */
export async function onDesktopUpdateStatus(cb: (s: UpdateStatus) => void): Promise<() => void> {
  if (!isDesktop()) return () => {};
  const [{ listen }, { invoke }] = await Promise.all([import("@tauri-apps/api/event"), import("@tauri-apps/api/core")]);
  const unlisten = await listen<UpdateStatus>("bundle:update-status", (e) => cb(e.payload));
  try {
    cb(await invoke<UpdateStatus>("update_get_status"));
  } catch {
    // Older desktop builds: only bundle:update-ready.
  }
  return unlisten;
}

/** Checks now ("Check for updates…"); progress and the result arrive as `bundle:update-status`. */
export async function checkDesktopUpdates(): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("update_check_now");
}

/** Tells the shell whether this window may be restarted for an update now (core Updates.safeToRestart). */
export async function reportDesktopActivity(safe: boolean): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("update_report_activity", { safe });
}

/** Installs the downloaded update and relaunches the app (rejects with `{ code, message }`). */
export async function installDesktopUpdate(): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("update_install_and_restart");
}

// ---- Sign-in through the system browser (INTEGRATION.md §7). Rust owns the session; the page never stores tokens ----

export type DesktopAuthErrorCode =
  | "signed_out"
  | "no_pending_sign_in"
  | "sign_in_expired"
  | "state_mismatch"
  | "invalid_code"
  | "cancelled"
  | "invalid_grant"
  | "network"
  | "server"
  | "browser"
  | "internal";

export interface DesktopAuthStatus {
  signedIn: boolean;
  userId: string | null;
  /** A browser sign-in was started and is waiting for the browser. */
  signingIn: boolean;
  /** Only on bundle:auth-changed after a failed attempt. */
  error?: DesktopAuthErrorCode;
}

/** What the auth commands reject with. */
export interface DesktopAuthError {
  code: DesktopAuthErrorCode;
  message: string;
}

export interface DesktopAccessToken {
  token: string;
  /** Epoch ms. */
  expiresAt: number;
  userId: string;
}

async function invokeDesktop<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args); // rejects with a DesktopAuthError
}

export const desktopAuthStatus = () => invokeDesktop<DesktopAuthStatus>("auth_status");
/** Cached in Rust and refreshed when < 60 s is left; `force` gets a new one (the API rejected the cached one). */
export const desktopAccessToken = (force = false) => invokeDesktop<DesktopAccessToken>("auth_get_access_token", force ? { force } : undefined);
export const desktopStartSignIn = () => invokeDesktop<void>("auth_start_sign_in");
export const desktopCompleteSignIn = (code: string) => invokeDesktop<DesktopAuthStatus>("auth_complete_with_code", { code });
export const desktopSignOut = () => invokeDesktop<DesktopAuthStatus>("auth_sign_out");

/** Sent to every window, so the global listen() is right here. */
export async function onDesktopAuthChanged(cb: (s: DesktopAuthStatus) => void): Promise<() => void> {
  if (!isDesktop()) return () => {};
  const { listen } = await import("@tauri-apps/api/event");
  return listen<DesktopAuthStatus>("bundle:auth-changed", (e) => cb(e.payload));
}

// ---- Shell events (INTEGRATION.md §4; routed by notifier.ts, which owns the workspace shell on screen) ----

/** `bundle:deep-link` payload and what `shell_ready` returns: `path` is the in-app route (null if none). */
export interface DeepLink {
  url: string;
  path: string | null;
}

/** `bundle:menu` payload: the macOS menu's File › Close Tab (⌘W), Bundle › About Bundle and Check for Updates…. */
export interface DesktopMenuAction {
  action: "close-tab" | "about" | "check-updates";
}

/** Linux desktop: WebKitGTK has no WebRTC, so calls (huddles and meetings) run in the system browser. */
export function callsInBrowser(): boolean {
  return desktopInfo()?.platform === "linux";
}

// ---- Windows (INTEGRATION.md §6: `open_window`) ----

/** "Open in new window" / tab dragged out of the window. Returns false in the browser. */
export async function openInNewWindow(path: string): Promise<boolean> {
  if (!isDesktop()) return false;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke<string>("open_window", { url: path });
  return true;
}

/**
 * Opens a URL in the system browser (the opener plugin's `open_url`, allowed by the default capability) — even a link to
 * the web app, which a click inside the app would open here (desktopLinks.ts).
 */
export async function openInBrowser(url: string): Promise<void> {
  if (!isDesktop()) {
    window.open(url, "_blank", "noopener");
    return;
  }
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("plugin:opener|open_url", { url });
}

// ---- Downloads (mail attachments): Rust saves into Downloads under a clean, unused name and opens with the default app ----

/** Older shells without the download commands reject with "Command download_save not found" (or not allowed). */
function missingCommand(e: unknown, cmd: string): boolean {
  const text = typeof e === "string" ? e : e instanceof Error ? e.message : JSON.stringify(e);
  return text.includes(cmd);
}

/**
 * Saves `bytes` in the user's Downloads (the name sanitised there, a clash numbered "name (1).pdf") → the saved path.
 * Null when the shell predates the command (the caller saves it the browser's way).
 */
export async function desktopSaveDownload(bytes: Uint8Array, name: string): Promise<string | null> {
  const { invoke } = await import("@tauri-apps/api/core");
  try {
    return await invoke<string>("download_save", bytes, { headers: { "x-bundle-filename": encodeURIComponent(name) } });
  } catch (e) {
    if (missingCommand(e, "download_save")) return null;
    throw e;
  }
}

/** Opens a file this app saved with the system's default app. */
export const desktopOpenDownload = (path: string) => invokeDesktop<void>("download_open", { path });
/** Shows a file this app saved in Finder / Explorer / the file manager. */
export const desktopRevealDownload = (path: string) => invokeDesktop<void>("download_reveal", { path });
